<template>
  <div class="w-full border border-accent-200 rounded-lg overflow-hidden">
    <ChartsPageSkeleton v-if="isLoading" />
    <div :class="{ hidden: isLoading }">
      <div class="w-full bg-white">
        <div class="p-6">
          <div class="w-full flex justify-between items-center">
            <SearchDropdown
              :saved-searches="savedSearchStore.searches"
              :selected-search="savedSearchStore.selectedSearch"
              :is-editable="false"
              :show-blank-name-error="savedSearchStore.showSearchNameBlankError"
              :is-comparison-set="
                savedSearchStore.selectedSearch?.is_comparison
              "
              :show-new-search-button="
                !savedSearchStore.onMentionsPageCheck &&
                !savedSearchStore.onChartsPageCheck
              "
              @selected="savedSearchStore.updateSearchSelection"
              @update="savedSearchStore.updateSearchName"
            />
            <MonitorToolbar
              data-intercom-target="Generate Analytics Presentation"
              :start-date="savedSearchStore.startDate"
              :end-date="savedSearchStore.endDate"
              :has-hamburger-menu="true"
              :has-custom-button="true"
              :custom-button-text="t('charts.generateAnalyticsPresentation')"
              :custom-button-icon="['far', 'file-chart-pie']"
              @custom-action="showPptReportDialog = true"
              @update-date-range="
                (newDateRange) => savedSearchStore.updateDateRange(newDateRange)
              "
            >
              <div class="m-2 text-accent-800 flex flex-col">
                <span
                  class="cursor-pointer hover:bg-accent-200 p-3"
                  data-intercom-target="Save Analytics layout as preset"
                  @click.stop="openDialog('saveLayout')"
                >
                  {{ t("charts.saveLayout") }}</span
                >
              </div>
              <div class="m-2 text-accent-800 flex flex-col">
                <span
                  class="cursor-pointer hover:bg-accent-200 p-3"
                  data-intercom-target="Apply Analytics preset"
                  @click.stop="openDialog('applyLayout')"
                >
                  {{ t("charts.applyLayout") }}</span
                >
              </div>
            </MonitorToolbar>
          </div>
          <div
            v-if="
              savedSearchStore.nestedSearches.length &&
              savedSearchStore.selectedSearch?.is_comparison
            "
          >
            <SearchController
              class="mt-6"
              :nested-searches="savedSearchStore.nestedSearches"
              :selected-nested-search="savedSearchStore.selectedNestedSearch"
              @search-selected="savedSearchStore.updateSelectedNestedSearch"
            />
          </div>
        </div>
      </div>
      <div ref="chartsAnalyticsContainer" class="w-full px-4 pb-4 bg-white">
        <VueDraggableNext
          v-if="displayCharts"
          class="gap-2 flex flex-wrap"
          :list="charts"
          handle=".drag-handle"
          @end="handleChartPositionChange"
        >
          <CustomChart
            v-for="chart in charts"
            :key="chart.id"
            class="transition-all shadow-popover"
            :class="
              chartsAnalyticsContainerWidth === 0
                ? getChartWidth(chart.attributes.columns)
                : ''
            "
            :style="
              chartsAnalyticsContainerWidth > 0
                ? {
                    width: `${getChartWidth(chart.attributes.columns, chartsAnalyticsContainerWidth, 8)}px`,
                  }
                : {}
            "
            :chart-id="Number(chart.id)"
            :chart-size="chart.attributes.columns"
            :type="chart.attributes.chart_type"
            :slice-by="chart.attributes.slice_by"
            :metric="chart.attributes.metric"
            :position="chart.attributes.position"
            :show-chart-analytics-wrapper="true"
            :title="translateDefaultChartAttribute(chart.attributes.title)"
            :tooltip-text="
              translateDefaultChartAttribute(chart.attributes.tooltip)
            "
            :data-text="`${chart.id}-${chart.attributes.chart_type}`"
            :read-only="false"
            @handle-edit="handleAddChart"
            @change-chart-size="handleChartSizeChange"
          />
          <div
            class="flex items-center justify-center bg-secondary-50 border rounded border-secondary-200 cursor-pointer w-7 p-4"
            :class="{ hidden: showVerticalAddButton }"
            data-intercom-target="Add chart button"
            @click="showChartEditor = true"
          >
            <ClientOnly>
              <font-awesome-icon :icon="['far', 'plus']" />
            </ClientOnly>
          </div>
        </VueDraggableNext>
        <EmptyState
          v-else
          :img-path="'/images/blank-charts.svg'"
          :data-text="'no-chart-analytics'"
          :heading="''"
          :text-array="[]"
        >
          <span class="mt-8">{{ t("charts.blankMsg1") }}</span>
          <span v-html="emptyStateMessage" />
        </EmptyState>
        <div
          class="flex items-center justify-center bg-secondary-50 border rounded border-secondary-200 cursor-pointer box-border py-4 h-6 mt-4"
          :class="{ hidden: !showVerticalAddButton }"
          @click="showChartEditor = true"
        >
          <ClientOnly>
            <font-awesome-icon :icon="['far', 'plus']" />
          </ClientOnly>
        </div>
      </div>
      <ChartAnalyticsDialog
        v-if="showDialog"
        :dialog-type="dialogType"
        :is-confirm-button-disabled="isConfirmButtonDisabled"
        :is-confirm-button-loading="isConfirmButtonLoading"
        :label="t('charts.preset')"
        :options="chartTemplates"
        :selected="selectedTemplate"
        :validator="isValidTemplate"
        :validation-error="isInvalidTemplateError"
        @add-custom="addToChartTemplates"
        @close="closeDialog"
        @confirm="handleConfirm"
        @option-selected="
          (newSelectedOpt: ListItem) => (selectedTemplate = newSelectedOpt)
        "
      />
      <ChartEditor
        v-if="showChartEditor"
        :chart-id="0"
        :chart-size="1"
        :chart-type="'line'"
        :dialog-type="'add'"
        :slice-by="'time'"
        :metric="'mentions'"
        :chart-title="t('analytics_chart.mentions.over_time.title')"
        :chart-tooltip="t('analytics_chart.mentions.over_time.tooltip')"
        :initial-metric="{
          id: 0,
          key: 'mentions',
        }"
        :initial-slice-by="{
          id: 0,
          key: 'time',
        }"
        @add="handleAddChart"
        @cancel="showChartEditor = !showChartEditor"
      />
      <Notification
        :show="showNotification"
        :type="notificationType"
        :title="$t(notificationText)"
        :show-icon="true"
        :show-accept="false"
        :show-cancel="false"
        :show-close="true"
        @close="resetNotification"
      />
      <PptReportDialog
        v-if="showPptReportDialog"
        @close="showPptReportDialog = false"
        @success="handlePptReportSuccess"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed, Ref, ref } from "vue";
import { useNuxtApp } from "nuxt/app";
import { VueDraggableNext } from "vue-draggable-next";
import { useTask, timeout } from "vue-concurrency";
import { useI18n } from "vue-i18n";
import { type ListItem } from "@airpr/360-ui-components";
import SearchDropdown from "~/components/search/SearchDropdown.vue";
import MonitorToolbar from "~/components/monitor/MonitorToolbar.vue";
import CustomChart from "~/components/shared/CustomChart.vue";
import EmptyState from "~/components/shared/EmptyState.vue";
import Notification from "~/components/shared/custom-notification/CustomNotification.vue";
import ChartAnalyticsDialog from "~/components/charts/ChartAnalyticsDialog.vue";
import SearchController from "~/components/search/SearchController.vue";
import ChartEditor from "~/components/dialogs/ChartEditor.vue";
import PptReportDialog from "~/components/dialogs/PptReportDialog.vue";
import { useChartTemplate } from "~/composables/useChartTemplate";
import { useChartOperations } from "~/composables/useChartOperations";
import { retrieveSessionDateRange } from "~/utils/RetrieveSessionDateRange";
import { replaceAsterisks } from "~/utils/replaceAsterisks";
import { useSearchStore } from "~/store/search.ts";
import { useUserStore } from "@/store/user";
import { useChartsStore } from "~/store/charts.ts";
import {
  ChartTemplate,
  DialogType,
  ChartSizeChangePayload,
  AddChartPayload,
  AnalyticsChart,
} from "~/types/ChartsType";
import Emitter from "~/types/Emitter";
import ChartsPageSkeleton from "~/components/charts/ChartsPageSkeleton.vue";
import { useMediaListStore } from "~/store/mediaList";

const savedSearchStore = useSearchStore();
const userStore = useUserStore();
const chartsStore = useChartsStore();
const { saveCustomTemplate, applyCustomTemplate } = useChartTemplate();
const {
  addChart,
  changeChartSize,
  getChartWidth,
  translateDefaultChartAttribute,
  changeChartPosition,
} = useChartOperations();
const mediaListStore = useMediaListStore();

const user = process.client ? userStore.getUser : null;

const { $bus }: Emitter = useNuxtApp();

const { t } = useI18n();

const dialogType: Ref<DialogType> = ref("saveLayout");
const searchLoaded: Ref<Boolean> = ref(true);
const showDialog: Ref<Boolean> = ref(false);
const isConfirmButtonLoading: Ref<boolean> = ref(false);
const selectedTemplate: Ref<ListItem | undefined> = ref(undefined);
const showChartEditor = ref(false);
const showPptReportDialog = ref(false);
const chartsAnalyticsContainer = ref<HTMLElement | null>(null);
const chartsAnalyticsContainerWidth = ref(0);
let resizeObserver: ResizeObserver | null = null;
const isLoading = ref(true);

const showNotification = ref(false);
const notificationText = ref("");
const notificationType = ref("default");

const chartTemplates = computed(() => {
  const savedChartsAnalyticsTemplates = chartsStore.chartTemplates
    .filter(
      (chartTemplate: ChartTemplate) =>
        !chartTemplate.attributes.is_default &&
        chartTemplate.attributes.name?.trim() !== "" &&
        chartTemplate.attributes.organization_id &&
        !chartTemplate.attributes.search_dashboard_id,
    )
    .map((chartTemplate: ChartTemplate) => {
      return {
        id: chartTemplate.id,
        name: chartTemplate.attributes.name,
      };
    });

  return savedChartsAnalyticsTemplates;
});

const charts = computed(() => {
  return chartsStore.analyticsCharts.get(chartsStore.selectedChartTemplate?.id);
});

const showVerticalAddButton = computed(() => {
  // Return boolean value on which version of the Add button to display
  if (chartsStore.selectedChartTemplate) {
    const MAX_COLUMNS = 3; // Number of columns in the grid
    let currentRowWidth = 0;

    charts.value?.forEach((chart: AnalyticsChart) => {
      const chartWidth = chart?.attributes.columns;

      // If the chart doesn't fit in the current row, move to the next row
      if (currentRowWidth + chartWidth > MAX_COLUMNS) {
        currentRowWidth = chartWidth; // Start a new row with this chart
      } else {
        currentRowWidth += chartWidth; // Add the chart to the current row
      }
    });

    // Check if there's space in the last row for a 1-column-wide chart
    // If there isn't, we need to show the vertical button
    return !(currentRowWidth + 1 <= MAX_COLUMNS);
  } else {
    return true;
  }
});

const emptyStateMessage = computed(() => {
  const replacements = ["<a class='text-success-150' href='/search'>", "</a>"];

  const translation = t("charts.blankMsg2");

  return replaceAsterisks(translation, replacements);
});

const isConfirmButtonDisabled = computed(() => {
  return !selectedTemplate.value;
});

const displayCharts = computed(() => {
  return chartsStore.analyticsCharts.size > 0;
});

const handlePptReportSuccess = () => {
  setNotification(t("pptReport.ready"), "success");
};

const handleChartSizeChange = ({ chartId, size }: ChartSizeChangePayload) => {
  const callback = (chartArray = null) => {
    // Update charts on the DB
    // Cancel any existing task instance before starting a new one
    saveTemplateTask.cancelAll();
    saveTemplateTask.perform(chartArray);
  };
  changeChartSize({ chartId, size, callback });
};

const addToChartTemplates = (newChartTemplate: ListItem) => {
  selectedTemplate.value = newChartTemplate;
};

const openDialog = (newDialogType: DialogType) => {
  $bus.$emit("CloseHamburgerButton");

  // Set the selected option only when saving or updating and the template is not default
  if (
    newDialogType === "saveLayout" &&
    !chartsStore.selectedChartTemplate.attributes.is_default
  ) {
    selectedTemplate.value = chartTemplates.value.find(
      (template: ListItem) =>
        Number(template.id) === Number(chartsStore.selectedChartTemplate.id),
    );
  }

  dialogType.value = newDialogType;
  showDialog.value = true;
};

const closeDialog = () => {
  showDialog.value = false;
  selectedTemplate.value = undefined;
};

const isValidTemplate = (name: string) => {
  return !chartTemplates.value.some(
    (chartTemplate: ListItem) =>
      chartTemplate.name.toLowerCase() === name.toLowerCase(),
  );
};

const isInvalidTemplateError = () => {
  return t("charts.chartsAnalyticsTemplateAlreadyExists");
};

const handleConfirm = async () => {
  if (dialogType.value === "saveLayout") {
    await handleSaveCustomTemplate();
  }

  if (dialogType.value === "applyLayout") {
    await applyTemplate();
  }
};

const handleSaveCustomTemplate = async () => {
  isConfirmButtonLoading.value = true;
  const callback = (errorMessage: string | null) => {
    isConfirmButtonLoading.value = false;
    closeDialog();

    // Show success or error notification
    setNotification(
      errorMessage ? t(errorMessage) : t("charts.chartsAnalyticsSuccess"),
      errorMessage ? "error" : "success",
    );
  };
  await saveCustomTemplate(selectedTemplate.value, callback);
};

const handleAddChart = async (payload: AddChartPayload) => {
  // Handles both Add and Edit of Charts
  const callback = (_payload: AddChartPayload, errorMessage: string) => {
    showChartEditor.value = false;
    // Shwifty: triggers redraw in child chart component
    if (_payload.callback) _payload.callback();

    const msg =
      _payload.props.dialogType === "edit"
        ? t("charts.chartEditSuccess")
        : t("charts.chartAddSuccess");

    // Show success or error notification
    setNotification(
      errorMessage ? t(errorMessage) : msg,
      errorMessage ? "error" : "success",
    );
  };
  await addChart(payload, callback);
};

const applyTemplate = async () => {
  isConfirmButtonLoading.value = true;
  const callback = (errorMessage: string) => {
    isConfirmButtonLoading.value = false;
    closeDialog();

    // Show success or error notification
    setNotification(
      errorMessage
        ? t(errorMessage)
        : t("charts.chartsAnalyticsTemplateSuccessfullyApplied"),
      errorMessage ? "error" : "success",
    );
  };
  await applyCustomTemplate(selectedTemplate.value, callback);
};

const handleChartPositionChange = () => {
  const callback = () => {
    // Update charts on the DB
    // Cancel any existing task instance before starting a new one
    saveTemplateTask.cancelAll();
    saveTemplateTask.perform();
  };
  changeChartPosition(callback);
};

const setNotification = (message: string, type: string) => {
  notificationText.value = message;
  notificationType.value = type;
  showNotification.value = true;
};

const resetNotification = () => {
  showNotification.value = false;
  notificationText.value = "";
  notificationType.value = "default";
};

const saveTemplateTask = useTask(
  function* (_, chartArray = null) {
    yield timeout(500);

    try {
      // Find selected template in the store
      selectedTemplate.value = chartsStore.chartTemplates
        .map((chartTemplate: ChartTemplate) => {
          return {
            id: chartTemplate.id,
            name: chartTemplate.attributes.name,
          };
        })
        .find(
          (template: ListItem) =>
            Number(template.id) ===
            Number(chartsStore.selectedChartTemplate.id),
        );

      if (selectedTemplate.value) {
        // NOTE: A record is considered "User Default" in the following cases:
        // 1. The current template is one of the app defaults (id is 1 or 2).
        // 2. The current template is a previously "User Default" created one (name is empty).
        const isUserDefaultTemplate =
          Number(selectedTemplate.value.id) <= 2 ||
          !selectedTemplate.value.name;

        yield chartsStore.saveTemplate(
          selectedTemplate.value,
          isUserDefaultTemplate,
          chartArray,
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setNotification(t("charts.chartsAnalyticsTemplateSaveError"), "error");
    } finally {
      selectedTemplate.value = undefined;
    }
  },
  { cancelOnUnmount: true },
);

onMounted(async () => {
  const dateRange = retrieveSessionDateRange();
  savedSearchStore.startDate = new Date(dateRange.start);
  savedSearchStore.endDate = new Date(dateRange.end);

  await savedSearchStore.fetchSearches();
  await savedSearchStore.fetchSearchFilters();
  await mediaListStore.fetchMediaLists();

  if (user) {
    const currentSearchDashboard = JSON.parse(
      localStorage.getItem("currentSearchDashboard") || "{}",
    );

    if (!currentSearchDashboard.data) {
      await savedSearchStore.updateSearchSelection(
        savedSearchStore.searches[0],
      );
    } else {
      const comparisonSet = savedSearchStore.getComparisonSetById(
        currentSearchDashboard.data.id,
      );

      if (comparisonSet) {
        await savedSearchStore.updateSearchSelection(comparisonSet);
      }
    }
  }

  if (chartsAnalyticsContainer.value) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chartsAnalyticsContainerWidth.value = Math.floor(
          entry.contentRect.width,
        );
      }
    });

    resizeObserver.observe(chartsAnalyticsContainer.value);
  }

  searchLoaded.value = true;
  isLoading.value = false;
});

onUnmounted(() => {
  if (resizeObserver && chartsAnalyticsContainer.value) {
    resizeObserver.unobserve(chartsAnalyticsContainer.value);
  }
});
</script>
